import React, { Component } from "react";
import "./packageLeadImport.css";
import Select from "react-select";
import * as XLSX from "xlsx";
import {
  Grid,
  Row,
  Col,
  FormControl,
  FormGroup,
  ControlLabel,
  Table,
} from "react-bootstrap";
import NotificationSystem from "react-notification-system";
import Card from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton.jsx";
import { style } from "../../../variables/Variables";
import { getApiErrorMessage } from "utils/getApiErrorMessage";
import { createPackageImportWorker, importRequestId } from "utils/packageLeadImportWorker";
import {
  createPackageLeadImport,
  uploadPackageLeadImportRowsChunk,
  applyPackageLeadImportStoredRows,
  getPackageLeadImportStatus,
  getPackageLeadImportHistory,
  getPackagesWithoutPagination,
  getPackageOrganizations,
  createPackageOnlyImport,
  uploadPackageOnlyRowsChunk,
  applyPackageOnlyImport,
  movePackageLeadsToBasic,
  resumePackageLeadOperation,
  deletePackageLeadTask,
  downloadPackageSkippedReport,
} from "api/packageLeadImport/packageLeadImport";

const baseFieldLabels = {
  mobile_no: "Mobile Number",
  name: "Name",
  email: "Email",
  policy_number: "Policy Number",
  gender: "Gender",
  date_of_birth: "Date of Birth",
};

const familyFieldLabels = {
  family_member_name: "Family Member Name",
  relationship: "Relationship",
  family_member_mobile: "Family Member Mobile Number",
  family_member_dob: "Family Member Date of Birth",
};

const mappingAliases = {
  package_name: ["applicablepackage", "packagename", "cataloguepackage", "catalogpackage", "package"],
  mobile_no: ["mobile", "mobilenumber", "mobileno", "phone", "phonenumber", "contactnumber", "customerphone"],
  name: ["name", "customername", "fullname", "membername", "patientname"],
  email: ["email", "emailaddress", "customeremail"],
  policy_number: ["policynumber", "policyno", "policy", "memberid"],
  gender: ["gender", "sex"],
  date_of_birth: ["dateofbirth", "dob", "birthdate", "birthday"],
  claim_value: ["claimvalue", "claimamount", "walletvalue", "creditvalue", "benefitvalue", "ridersumassured", "sumassured"],
  family_member_name: ["familymembername", "dependentname", "membername"],
  relationship: ["relationship", "relation"],
  family_member_mobile: ["familymembermobilenumber", "familymembermobile", "dependentmobile", "dependentphone"],
  family_member_dob: ["familymemberdob", "dependentdob", "dob", "dateofbirth"],
};

const normalizeHeaderName = (header) =>
  String(header || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const packageOperationErrorMessage = (error) => {
  if (error && !error.response && /network error|failed to fetch|load failed/i.test(error.message || "")) {
    return "Could not reach the package API. Check your connection and the failed request in Network.";
  }
  const details = error && error.response && error.response.data && error.response.data.data;
  if (details && typeof details === "object") {
    const values = Object.values(details);
    for (const value of values) {
      if (typeof value === "string" && value.trim()) { return value; }
      if (Array.isArray(value) && typeof value[0] === "string") { return value[0]; }
    }
  }
  return getApiErrorMessage(error);
};

class PackageLeadImport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      packages: [],
      organizations: [],
      importType: "package_only",
      packageId: "",
      packageEndDate: "",
      importId: "",
      headers: [],
      mapping: {
        mobile_no: "",
        name: "",
        email: "",
        policy_number: "",
        gender: "",
        date_of_birth: "",
        claim_value: "",
        family_member_name: "",
        relationship: "",
        family_member_mobile: "",
        family_member_dob: "",
      },
      preview: null,
      applyStatus: "",
      applySummary: null,
      applyProgress: null,
      applyRows: [],
      issueRows: [],
      importHistory: [],
      historyLoading: false,
      deletingTask: "",
      uploadReady: false,
      uploadProgress: 0,
      rowsUploadProgress: 0,
      importLocked: false,
      applying: false,
      uploading: false,
      movingToBasic: false,
      basicImportId: "",
      basicStatus: "",
      basicSummary: null,
      basicSkippedRows: [],
      basicError: "",
      validationProgress: 0,
    };
  }

  componentDidMount() {
    this.setState({ _notificationSystem: this.refs.notificationSystem });
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user || (user.role != 1 && user.role != 2)) {
      this.props.history.push("/dashboard");
      return;
    }

    getPackagesWithoutPagination(user.token)
      .then((packageRes) => {
        const packages = packageRes && packageRes.data ? packageRes.data : [];
        this.setState({ packages });
      })
      .catch((error) => {
        this.notify(
          "error",
          getApiErrorMessage(error) || "Unable to load packages.",
        );
      });

    this.loadImportHistory();
    this.historyTimer = setInterval(this.loadImportHistory, 30000);
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.packageWorker) { this.packageWorker.terminate(); }
    if (this.basicStatusTimer) { clearTimeout(this.basicStatusTimer); }
    if (this.applyStatusTimer) {
      clearTimeout(this.applyStatusTimer);
    }
    if (this.headerStatusTimer) {
      clearTimeout(this.headerStatusTimer);
    }
    if (this.historyTimer) {
      clearInterval(this.historyTimer);
    }
  }

  notify(level, message) {
    if (!this.state._notificationSystem) {
      return;
    }

    this.state._notificationSystem.addNotification({
      title: (
        <span
          data-notify="icon"
          className={level === "success" ? "pe-7s-check" : "pe-7s-close-circle"}
        />
      ),
      message: <div>{message}</div>,
      level,
      position: "bl",
      autoDismiss: 10,
    });
  }

  token() {
    const user = JSON.parse(localStorage.getItem("user"));
    return user ? user.token : "";
  }

  handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "importType" && (value === "package_only" || this.state.importType === "package_only")) {
      this.resetImport();
    }
    this.setState((prevState) => {
      const nextState = { [name]: value, preview: null };
      if (
        name === "importType" &&
        value === "package_family_wallet" &&
        prevState.mapping.date_of_birth &&
        prevState.mapping.date_of_birth === prevState.mapping.family_member_dob
      ) {
        nextState.mapping = {
          ...prevState.mapping,
          date_of_birth: "",
        };
      }

      return nextState;
    });

    if (name === "packageId") {
      this.loadPackageOrganizations(value);
    }
  };

  loadImportHistory = () => {
    const token = this.token();
    if (!token) {
      return;
    }

    this.setState({ historyLoading: true });
    getPackageLeadImportHistory(token, 20)
      .then((res) => {
        this.setState({
          importHistory: res.data || [],
          historyLoading: false,
        });
      })
      .catch(() => {
        this.setState({ historyLoading: false });
      });
  };

  loadPackageOrganizations(packageId) {
    this.setState({
      organizations: [],
      preview: null,
      importId: "",
      headers: [],
      uploadReady: false,
      uploadProgress: 0,
    });

    if (!packageId) {
      return;
    }

    getPackageOrganizations(this.token(), packageId)
      .then((res) => {
        const organizations = res && res.data ? res.data : [];
        this.setState({ organizations });
      })
      .catch((error) => {
        this.notify(
          "error",
          getApiErrorMessage(error) || "Unable to load package organization.",
        );
      });
  }

  packageOptions() {
    return this.state.packages.map((pkg) => ({
      value: String(pkg.id),
      label: pkg.title,
    }));
  }

  selectedPackageOption() {
    const packageId = String(this.state.packageId || "");
    return (
      this.packageOptions().find((option) => option.value === packageId) || null
    );
  }

  handlePackageSelectChange = (selectedOption) => {
    const packageId = selectedOption ? selectedOption.value : "";
    if (packageId !== this.state.packageId) {
      if (this.basicStatusTimer) { clearTimeout(this.basicStatusTimer); }
      this.basicRequest = null;
      this.setState({ basicImportId: "", basicStatus: "", basicSummary: null, basicSkippedRows: [], basicError: "" });
    }
    this.setState({
      packageId,
      preview: null,
    });
    if (this.state.importType !== "package_only") { this.loadPackageOrganizations(packageId); }
  };

  handleMappingChange = (field, value) => {
    this.setState((prevState) => ({
      mapping: {
        ...prevState.mapping,
        [field]: value,
      },
    }));
  };

  handleFileChange = (event) => {
    this.setState({
      file: event.target.files[0],
      importId: "",
      headers: [],
      preview: null,
      applyStatus: "",
      applySummary: null,
      applyProgress: null,
      applyRows: [],
      issueRows: [],
      uploadReady: false,
      uploadProgress: 0,
      rowsUploadProgress: 0,
      importLocked: false,
    });
  };

  upload = () => {
    if (!this.state.file) {
      this.notify("error", "Please choose an Excel file.");
      return;
    }
    if (this.state.importType !== "package_only" && !this.state.packageId) {
      this.notify("error", "Please select a package.");
      return;
    }
    if (!this.state.packageEndDate) {
      this.notify("error", "Please select package end date.");
      return;
    }

    this.setState({
      uploading: true,
      preview: null,
      importId: "",
      headers: [],
      uploadReady: false,
      uploadProgress: 0,
      rowsUploadProgress: 0,
      importLocked: false,
    });

    Promise.all([
      this.readHeadersFromFile(this.state.file),
      this.state.importType === "package_only" ? Promise.resolve({ data: {} }) : createPackageLeadImport(this.token(), {
        file_name: this.state.file.name,
        package_id: this.state.packageId,
      }),
    ])
      .then((res) => {
        const headers = res[0] || [];
        const data = (res[1] && res[1].data) || {};
        const suggestedMapping = this.suggestMapping(headers);
        if (
          this.state.importType === "package_family_wallet" &&
          suggestedMapping.date_of_birth &&
          suggestedMapping.date_of_birth === suggestedMapping.family_member_dob
        ) {
          suggestedMapping.date_of_birth = "";
        }

        this.setState({
          importId: data.import_id,
          headers,
          preview: null,
          uploadReady: this.state.importType === "package_only" || !!data.import_id,
          mapping: {
            ...this.state.mapping,
            ...suggestedMapping,
          },
          uploadProgress: 100,
        });
        this.notify("success", "Columns are ready. Confirm the mapping.");
      })
      .catch((error) => {
        this.notify(
          "error",
          getApiErrorMessage(error) || "Unable to prepare this Excel import.",
        );
      })
      .then(() => this.setState({ uploading: false }));
  };

  readHeadersFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const workbook = XLSX.read(new Uint8Array(event.target.result), {
            type: "array",
            sheetRows: 1,
          });
          const firstSheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[firstSheetName];
          const firstRow = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            blankrows: false,
          })[0] || [];
          const headers = firstRow.map((header, index) => ({
            index,
            name: String(header || "").trim(),
          }));

          resolve(headers);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  suggestMapping(headers) {
    const mapping = {};
    headers.forEach((header) => {
      const normalized = normalizeHeaderName(header.name);
      Object.keys(mappingAliases).forEach((field) => {
        if (!mapping[field] && mappingAliases[field].indexOf(normalized) !== -1) {
          mapping[field] = header.name;
        }
      });
    });

    return mapping;
  }

  pollHeaderStatus = () => {
    if (!this.state.importId) {
      return;
    }

    getPackageLeadImportStatus(this.token(), this.state.importId)
      .then((res) => {
        const data = res.data || {};
        if (data.status === "uploaded" && data.headers && data.headers.length > 0) {
          const suggestedMapping = data.suggested_mapping || {};
          if (
            this.state.importType === "package_family_wallet" &&
            suggestedMapping.date_of_birth &&
            suggestedMapping.date_of_birth === suggestedMapping.family_member_dob
          ) {
            suggestedMapping.date_of_birth = "";
          }

          this.setState({
            headers: data.headers && data.headers.length > 0 ? data.headers : this.state.headers,
            preview: null,
            uploadReady: true,
            mapping: {
              ...this.state.mapping,
              ...suggestedMapping,
            },
          });
          this.notify("success", "Columns are ready. Confirm the mapping.");
          return;
        }

        if (data.status === "headers_failed" || data.status === "upload_failed") {
          this.setState({ preview: { status: data.status }, uploadReady: false });
          this.notify(
            "error",
            data.headers_error || data.apply_error || "Upload preparation failed.",
          );
          return;
        }

        this.setState({ preview: { status: data.status || "headers_processing" } });
        this.headerStatusTimer = setTimeout(this.pollHeaderStatus, 5000);
      })
      .catch(() => {
        this.headerStatusTimer = setTimeout(this.pollHeaderStatus, 10000);
      });
  };

  buildPayload() {
    return {
      import_id: this.state.importId,
      package_id: this.state.packageId,
      import_type: this.state.importType,
      package_end_date: this.state.packageEndDate || null,
      mapping: this.state.mapping,
    };
  }

  isImportLocked() {
    const lockedStatuses = [
      "reading_rows",
      "uploading_rows",
      "rows_uploaded",
      "apply_prepare_queued",
      "processing_batches",
      "completed",
      "apply_queued",
      "applying",
    ];

    return (
      this.state.importLocked ||
      this.state.movingToBasic ||
      this.state.uploading ||
      this.state.applying ||
      lockedStatuses.indexOf(this.state.applyStatus) !== -1
    );
  }

  resetImport = () => {
    this.setState({
      file: null,
      importId: "",
      headers: [],
      mapping: {
        mobile_no: "",
        name: "",
        email: "",
        policy_number: "",
        gender: "",
        date_of_birth: "",
        claim_value: "",
        family_member_name: "",
        relationship: "",
        family_member_mobile: "",
        family_member_dob: "",
      },
      preview: null,
      applyStatus: "",
      applySummary: null,
      applyProgress: null,
      applyRows: [],
      issueRows: [],
      uploadReady: false,
      uploadProgress: 0,
      rowsUploadProgress: 0,
      importLocked: false,
      applying: false,
      uploading: false,
    });
  };

  canStartNewImport() {
    return ["completed", "apply_failed", "upload_failed", "cancelled"].indexOf(this.state.applyStatus) !== -1;
  }

  formatMappedDate(value) {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value === "number") {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (parsed && parsed.y && parsed.m && parsed.d) {
        return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`;
      }
    }

    const text = String(value).trim();
    if (!text) {
      return "";
    }
    const parsedDate = new Date(text);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().slice(0, 10);
    }

    return text;
  }

  normalizeMappedGender(value) {
    const text = String(value || "").trim().toLowerCase();
    if (["m", "male"].indexOf(text) !== -1) {
      return "male";
    }
    if (["f", "female"].indexOf(text) !== -1) {
      return "female";
    }
    return text;
  }

  normalizeMappedAmount(value) {
    const text = String(value || "").replace(/,/g, "").trim();
    if (!text) {
      return "";
    }
    const amount = Number(text);
    return Number.isFinite(amount) ? amount : text;
  }

  normalizeMappedMobile(value) {
    let mobile = String(value || "").trim().replace(/[\s\-()]/g, "");
    if (!mobile) {
      return "";
    }

    if (mobile.indexOf("+94") === 0) {
      mobile = mobile.slice(3);
    } else if (mobile.indexOf("94") === 0) {
      mobile = mobile.slice(2);
    } else if (mobile.indexOf("0") === 0) {
      mobile = mobile.slice(1);
    } else if (mobile.indexOf("+") === 0) {
      mobile = mobile.slice(1);
    }

    return /^[0-9]{9}$/.test(mobile) ? `+94${mobile}` : "";
  }

  readMappedRowsFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const workbook = XLSX.read(new Uint8Array(event.target.result), {
            type: "array",
            cellDates: true,
          });
          const firstSheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[firstSheetName];
          const sheetRows = XLSX.utils.sheet_to_json(sheet, {
            defval: "",
            blankrows: false,
          });
          const seen = {};
          const rows = sheetRows.map((sheetRow, index) => {
            const valueFor = (field) => {
              const column = this.state.mapping[field];
              return column ? sheetRow[column] : "";
            };
            const normalized = {
              mobile_no: this.normalizeMappedMobile(valueFor("mobile_no")),
              name: String(valueFor("name") || "").trim(),
              email: String(valueFor("email") || "").trim(),
              policy_number: String(valueFor("policy_number") || "").trim(),
              gender: this.normalizeMappedGender(valueFor("gender")),
              date_of_birth: this.formatMappedDate(valueFor("date_of_birth")),
              claim_value: this.normalizeMappedAmount(valueFor("claim_value")),
              family_member_name: String(valueFor("family_member_name") || "").trim(),
              relationship: String(valueFor("relationship") || "").trim(),
              family_member_mobile: this.normalizeMappedMobile(valueFor("family_member_mobile")),
              family_member_dob: this.formatMappedDate(valueFor("family_member_dob")),
            };
            const identity = normalized.mobile_no
              ? `mobile:${normalized.mobile_no}`
              : normalized.email
                ? `email:${normalized.email.toLowerCase()}`
                : normalized.policy_number
                  ? `policy:${normalized.policy_number.toLowerCase()}`
                  : "";
            const errors = [];
            if (!identity) {
              errors.push("Missing mobile number, email or policy number.");
            }
            if (identity && seen[identity]) {
              errors.push("Duplicate row in Excel.");
            }
            if (identity) {
              seen[identity] = true;
            }

            const familyMembers = [];
            if (
              this.state.importType === "package_family_wallet" &&
              (normalized.family_member_name ||
                normalized.relationship ||
                normalized.family_member_mobile ||
                normalized.family_member_dob)
            ) {
              familyMembers.push({
                row_number: index + 2,
                name: normalized.family_member_name,
                relation: normalized.relationship,
                phone: normalized.family_member_mobile,
                dob: normalized.family_member_dob,
              });
            }

            return {
              row_number: index + 2,
              status: errors.length > 0 ? (errors[0].indexOf("Duplicate") !== -1 ? "duplicate" : "invalid") : "valid",
              action: "pending",
              normalized,
              family_members: familyMembers,
              errors,
              package_end_date: this.state.packageEndDate || null,
            };
          });

          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  async uploadMappedRows(rows) {
    const chunkSize = 500;
    const totalChunks = Math.ceil(rows.length / chunkSize) || 1;

    for (let index = 0; index < totalChunks; index += 1) {
      const start = index * chunkSize;
      const chunk = rows.slice(start, start + chunkSize);
      await uploadPackageLeadImportRowsChunk(this.token(), {
        import_id: this.state.importId,
        package_id: this.state.packageId,
        import_type: this.state.importType,
        package_end_date: this.state.packageEndDate || null,
        chunk_index: index,
        rows: chunk,
      });
      this.setState({
        rowsUploadProgress: Math.round(((index + 1) / totalChunks) * 100),
        applyProgress: {
          total_rows: rows.length,
          processed_rows: Math.min((index + 1) * chunkSize, rows.length),
          successful_rows: 0,
          failed_rows: 0,
        },
      });
    }
  }

  selectedPackageHasClaimFeature() {
    const packageId = String(this.state.packageId || "");
    const selectedPackage = this.state.packages.find(
      (pkg) => String(pkg.id) === packageId,
    );

    const packageFeatures =
      selectedPackage &&
      (selectedPackage.package_feature ||
        selectedPackage.packageFeature ||
        selectedPackage.packageFeatures);

    if (!packageFeatures) {
      return false;
    }

    return packageFeatures.some(
      (feature) =>
        (feature.feature_title || feature.title) &&
        String(feature.feature_title || feature.title).toLowerCase() === "claim",
    );
  }

  mappingFields() {
    if (this.state.importType === "package_only") {
      return { ...baseFieldLabels, policy_number: "Policy Number (reference only)", package_name: "Package Name (exact catalogue name)" };
    }
    const fields = {
      ...baseFieldLabels,
      claim_value: this.selectedPackageHasClaimFeature()
        ? "Claim Wallet Value (optional)"
        : "Claim Wallet Value",
    };

    if (this.state.importType === "package_family_wallet") {
      return {
        ...fields,
        ...familyFieldLabels,
      };
    }

    return fields;
  }

  renderOrganizations() {
    if (!this.state.packageId) {
      return "-";
    }

    if (this.state.organizations.length === 0) {
      return "No organization assigned";
    }

    return this.state.organizations
      .map((assignment) =>
        assignment.b2b && assignment.b2b.company_name
          ? assignment.b2b.company_name
          : assignment.company_name || assignment.organization_id,
      )
      .join(", ");
  }

  apply = async () => {
    if (this.state.importType === "package_only") { return this.applyNamedPackages(); }
    if (!this.state.importId) {
      this.notify("error", "Please upload an Excel file first.");
      return;
    }
    if (!this.state.packageId) {
      this.notify("error", "Please select a package.");
      return;
    }
    if (this.state.headers.length === 0) {
      this.notify("error", "Please upload Excel and map columns first.");
      return;
    }
    const confirmed = window.confirm(
      "Apply import now? Processing will continue in the background.",
    );

    if (!confirmed) {
      return;
    }

    this.setState({
      applying: true,
      importLocked: true,
      applyStatus: "reading_rows",
      applySummary: null,
      applyProgress: null,
      applyRows: [],
      issueRows: [],
      preview: null,
      rowsUploadProgress: 0,
    });

    try {
      const rows = await this.readMappedRowsFromFile(this.state.file);
      this.setState({
        applyStatus: "uploading_rows",
        applyProgress: {
          total_rows: rows.length,
          processed_rows: 0,
          successful_rows: 0,
          failed_rows: 0,
        },
      });
      await this.uploadMappedRows(rows);
      const res = await applyPackageLeadImportStoredRows(this.token(), this.buildPayload());
      const data = res.data || {};
      this.setState({ applyStatus: data.status || "processing_batches" });
      this.notify("success", "Rows saved. Package lead import is running in the background.");
      this.loadImportHistory();
      this.pollApplyStatus();
    } catch (error) {
      this.setState({ applying: false });
      this.notify("error", getApiErrorMessage(error) || "Apply failed.");
    }
  };

  moveToBasic = async () => {
    if (this.isImportLocked()) { return; }
    if (!this.state.packageId || String(this.state.packageId) === "24") {
      this.notify("error", "Select a package other than BASIC.");
      return;
    }
    const selected = this.selectedPackageOption();
    if (!window.confirm(`Move all active members of ${selected.label} to BASIC? Their package benefits will be replaced. Wallet balances will remain unchanged.`)) { return; }
    if (!this.basicRequest || this.basicRequest.package_id !== this.state.packageId || this.state.basicStatus === "completed") {
      this.basicRequest = { package_id: this.state.packageId, request_id: importRequestId() };
    }
    this.setState({ movingToBasic: true, basicStatus: "starting", basicSummary: null, basicSkippedRows: [], basicError: "" });
    try {
      const res = await movePackageLeadsToBasic(this.token(), this.basicRequest);
      if (this.unmounted) { return; }
      this.setState({ basicImportId: res.data.import_id, basicStatus: res.data.status }, this.pollBasicStatus);
      this.loadImportHistory();
    } catch (error) {
      if (this.unmounted) { return; }
      const message = packageOperationErrorMessage(error) || "Unable to move this package to BASIC. Retry to resume.";
      this.setState({ movingToBasic: false, basicStatus: "request_failed", basicError: message });
      this.notify("error", message);
    }
  };

  pollBasicStatus = () => {
    if (!this.state.basicImportId || this.unmounted) { return; }
    return getPackageLeadImportStatus(this.token(), this.state.basicImportId).then((res) => {
      if (this.unmounted) { return; }
      const data = res.data || {};
      const finished = ["completed", "apply_failed", "cancelled"].indexOf(data.status) !== -1;
      const message = data.apply_error || data.error_message || "Move to BASIC stopped. Retry to resume.";
      this.setState({ basicStatus: data.status, basicSummary: data.apply_summary, movingToBasic: !finished,
        basicSkippedRows: data.rows || [],
        basicError: data.status === "apply_failed" ? message : "" });
      if (finished) {
        this.loadImportHistory();
        this.notify(data.status === "apply_failed" ? "error" : "success",
          data.status === "cancelled" ? "Task deleted." : data.status === "completed" ? "Package members moved to BASIC." : message);
      } else { this.basicStatusTimer = setTimeout(this.pollBasicStatus, 5000); }
    }).catch(() => {
      if (!this.unmounted) {
        this.setState({ basicError: "Could not refresh progress. The reset may still be running; wait for its completed status before importing." });
        this.basicStatusTimer = setTimeout(this.pollBasicStatus, 10000);
      }
    });
  };

  async applyNamedPackages() {
    if (this.isImportLocked()) { return; }
    if (!this.state.file || !this.state.headers.length || !this.state.mapping.package_name) {
      this.notify("error", "Choose Excel, click Upload and check the Package Name column.");
      return;
    }
    if (!this.state.packageEndDate) { this.notify("error", "Please select package end date."); return; }
    if (!window.confirm("Assign packages from this Excel without wallet credits? Existing package benefits for these customers will be replaced. Customers absent from the file will remain unchanged.")) { return; }
    this.setState({ applying: true, importLocked: true, applyStatus: "validating_file", validationProgress: 0, rowsUploadProgress: 0 });
    try {
      await this.uploadNamedPackagesFromWorker();
      if (this.unmounted) { return; }
      const res = await applyPackageOnlyImport(this.token(), {
        import_id: this.state.importId, package_end_date: this.state.packageEndDate,
      });
      this.setState({ applyStatus: res.data.status || "processing_batches" });
      this.loadImportHistory();
      this.pollApplyStatus();
    } catch (error) {
      if (this.unmounted) { return; }
      this.setState({ applying: false, importLocked: false, applyStatus: "upload_failed" });
      this.notify("error", packageOperationErrorMessage(error) || "Unable to process this file.");
    }
  }

  uploadNamedPackagesFromWorker() {
    return new Promise((resolve, reject) => {
      const { worker, sheetJsUrl } = createPackageImportWorker();
      this.packageWorker = worker;
      let importId, savedRows = 0;
      const stop = () => { worker.terminate(); this.packageWorker = null; };
      worker.onerror = (event) => { stop(); reject(new Error(event.message || "Unable to read Excel.")); };
      worker.onmessage = async (event) => {
        if (this.unmounted) { stop(); return; }
        const data = event.data;
        try {
          if (data.type === "error") { throw new Error(data.message); }
          if (data.type === "validating") { this.setState({ validationProgress: data.percent }); }
          if (data.type === "ready") {
            const res = await createPackageOnlyImport(this.token(), {
              file_name: this.state.file.name, expected_rows: data.expectedRows, total_chunks: data.totalChunks,
            });
            if (this.unmounted) { stop(); return; }
            importId = res.data.import_id;
            this.setState({ importId, applyStatus: "uploading_rows" });
            worker.postMessage({ type: "next" });
          }
          if (data.type === "chunk") {
            await uploadPackageOnlyRowsChunk(this.token(), {
              import_id: importId, chunk_index: data.chunkIndex,
              package_end_date: this.state.packageEndDate, rows: data.rows,
            });
            if (this.unmounted) { stop(); return; }
            savedRows += data.rows.length;
            this.setState({ rowsUploadProgress: Math.round(savedRows / data.expectedRows * 100) });
            worker.postMessage({ type: "next" });
          }
          if (data.type === "done") { stop(); resolve(); }
        } catch (error) { stop(); reject(error); }
      };
      const reader = new FileReader();
      reader.onerror = () => { stop(); reject(new Error("Unable to read the selected file.")); };
      reader.onload = () => {
        if (this.unmounted) { stop(); return; }
        worker.postMessage({ type: "init", buffer: reader.result, sheetJsUrl,
          mapping: this.state.mapping, packageTitles: this.state.packages.filter((pkg) => Number(pkg.is_active) !== 0).map((pkg) => pkg.title),
        }, [reader.result]);
      };
      reader.readAsArrayBuffer(this.state.file);
    });
  }

  pollApplyStatus = () => {
    if (!this.state.importId) {
      return;
    }

    getPackageLeadImportStatus(this.token(), this.state.importId)
      .then((res) => {
        if (this.unmounted) { return; }
        const data = res.data || {};
        const nextState = {
          applyStatus: data.status || "",
          applySummary: data.apply_summary || null,
          applyRows: data.rows || [],
          issueRows: data.issue_rows || [],
          importLocked: data.is_locked !== undefined ? data.is_locked : this.isImportLocked(),
          applyProgress: {
            total_rows: data.display_total_rows || data.total_rows || 0,
            processed_rows: data.display_processed_rows || data.processed_rows || 0,
            successful_rows: data.successful_rows || 0,
            failed_rows: data.failed_rows || 0,
          },
        };

        if (["completed", "apply_failed", "cancelled"].indexOf(data.status) !== -1) {
          nextState.applying = false;
          this.setState(nextState);
          this.loadImportHistory();
          const skippedCustomers = data.import_type === "package_only" && data.failed_rows > 0;
          this.notify(
            data.status === "apply_failed" || skippedCustomers ? "error" : "success",
            data.status === "cancelled" ? "Task deleted." : data.status === "completed"
              ? (skippedCustomers ? `Import finished: ${data.successful_rows || 0} assigned; ${data.failed_rows} skipped. Review the issues report.` : "Package lead import completed.")
              : data.apply_error || "Package lead import failed.",
          );
          return;
        }

        this.setState(nextState);
        this.applyStatusTimer = setTimeout(this.pollApplyStatus, 5000);
      })
      .catch(() => {
        if (!this.unmounted) { this.applyStatusTimer = setTimeout(this.pollApplyStatus, 10000); }
      });
  };

  viewOperation = async (item) => {
    if (["apply_failed", "rows_uploaded"].indexOf(item.status) !== -1) {
      if (!window.confirm(item.status === "rows_uploaded" ? "Apply the saved package assignments using the end date supplied at upload?" : "Resume this operation from its saved progress?")) { return; }
      try { await resumePackageLeadOperation(this.token(), { import_id: item.import_id }); }
      catch (error) { this.notify("error", packageOperationErrorMessage(error) || "Unable to resume."); return; }
    }
    if (item.import_type === "move_to_basic") {
      this.setState({ basicImportId: item.import_id, basicStatus: item.status, movingToBasic: item.status !== "completed" }, this.pollBasicStatus);
    } else {
      this.setState({ importId: item.import_id, importType: "package_only", applyStatus: item.status,
        applying: item.status !== "completed", importLocked: true, headers: [],
      }, this.pollApplyStatus);
    }
  };

  deleteTask = async (item) => {
    if (this.state.deletingTask) { return; }
    if (!window.confirm(`Delete task "${item.file_name || item.import_id}"? This removes it from history and stops future processing. Any assignments already completed will remain.`)) { return; }
    this.setState({ deletingTask: item.import_id });
    try {
      await deletePackageLeadTask(this.token(), { import_id: item.import_id });
      if (this.state.importId === item.import_id) { this.resetImport(); }
      if (this.state.basicImportId === item.import_id) {
        clearTimeout(this.basicStatusTimer);
        this.basicRequest = null;
        this.setState({ basicImportId: "", basicStatus: "", basicSummary: null, basicSkippedRows: [], basicError: "", movingToBasic: false });
      }
      this.setState({ importHistory: this.state.importHistory.filter((task) => task.import_id !== item.import_id) });
      this.loadImportHistory();
      this.notify("success", "Task deleted. Completed assignments remain unchanged.");
    } catch (error) {
      this.notify("error", packageOperationErrorMessage(error) || "Unable to delete task.");
    } finally { this.setState({ deletingTask: "" }); }
  };

  downloadSkippedReport = async (item) => {
    try {
      const blob = await downloadPackageSkippedReport(this.token(), item.import_id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.import_type === "package_only" ? "package-import-issues.csv" : "move-to-basic-skipped.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { this.notify("error", "Unable to download the skipped-members report. Please retry."); }
  };

  renderHeaderOptions() {
    return (
      <React.Fragment>
        <option value="">Not mapped</option>
        {this.state.headers.map((header) => (
          <option key={header.index} value={header.name}>
            {header.name || `Column ${header.index + 1}`}
          </option>
        ))}
      </React.Fragment>
    );
  }

  renderSummary() {
    if (!this.state.applySummary) {
      return null;
    }

    const summary = this.state.applySummary;
    const items = [
      ["Excel rows", summary.excel_row_count || summary.stored_row_count],
      ["Package assignment", `${summary.package_assignment_processed || 0} / ${summary.package_assignment_total || 0}`],
      ["Created leads", summary.created_count],
      ["Updated leads", summary.updated_count],
      ["Assigned package", summary.assigned_count],
      ["Family members saved", summary.family_members_saved_count],
      ["Wallet credits", `${summary.wallet_credit_success_count || 0} / ${summary.wallet_credit_attempted_count || 0}`],
      ["Moved to BASIC", `${summary.move_to_basic_processed || summary.downgraded_count || 0} / ${summary.move_to_basic_total || 0}`],
      ["Overall work", `${(summary.package_assignment_processed || 0) + (summary.move_to_basic_processed || 0)} / ${summary.total_work || summary.package_assignment_total || 0}`],
      ["Skipped rows", summary.skipped_count],
      ["Failed rows", summary.failed_count],
    ];

    return (
      <Table striped responsive>
        <tbody>
          {items.map((item) => (
            <tr key={item[0]}>
              <td>{item[0]}</td>
              <td>{item[1] || 0}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderApplyStatus() {
    if (!this.state.applyStatus) {
      return null;
    }

    const summary = this.state.applySummary || {};
    const progress = this.state.applyProgress || {};
    const assignmentTotal = summary.package_assignment_total || progress.total_rows || 0;
    const assignmentProcessed = summary.package_assignment_processed || progress.processed_rows || 0;
    const basicTotal = summary.move_to_basic_total || 0;
    const basicProcessed = summary.move_to_basic_processed || 0;
    return (
      <div style={{ marginTop: 15 }}>
        <strong>Status:</strong> {this.state.applyStatus}
        {this.state.applyStatus === "validating_file" && <div>Validating Excel: {this.state.validationProgress}%</div>}
        {this.state.rowsUploadProgress > 0 && this.state.rowsUploadProgress < 100 && (
          <div style={{ marginTop: 5 }}>
            Saving mapped rows: {this.state.rowsUploadProgress}%
          </div>
        )}
        {assignmentTotal > 0 && (
          <div style={{ marginTop: 5 }}>
            Package assignment: {assignmentProcessed} / {assignmentTotal}
          </div>
        )}
        {basicTotal > 0 && (
          <div style={{ marginTop: 5 }}>
            Move to BASIC: {basicProcessed} / {basicTotal}
          </div>
        )}
        {basicTotal > 0 && (
          <div style={{ marginTop: 5 }}>
            Overall work: {assignmentProcessed + basicProcessed} / {assignmentTotal + basicTotal}
          </div>
        )}
        {this.state.applySummary && (
          <Table striped responsive style={{ marginTop: 10 }}>
            <tbody>
              <tr>
                <td>Created leads</td>
                <td>{summary.created_count || 0}</td>
              </tr>
              <tr>
                <td>Updated leads</td>
                <td>{summary.updated_count || 0}</td>
              </tr>
              <tr>
                <td>Assigned package</td>
                <td>{summary.assigned_count || 0}</td>
              </tr>
              <tr>
                <td>Family members saved</td>
                <td>{summary.family_members_saved_count || 0}</td>
              </tr>
              <tr>
                <td>Wallet credits</td>
                <td>{summary.wallet_credit_success_count || 0} / {summary.wallet_credit_attempted_count || 0}</td>
              </tr>
              <tr>
                <td>Moved to BASIC</td>
                <td>{summary.move_to_basic_processed || summary.downgraded_count || 0} / {summary.move_to_basic_total || 0}</td>
              </tr>
              <tr>
                <td>Failed rows</td>
                <td>{summary.failed_count || 0}</td>
              </tr>
            </tbody>
          </Table>
        )}
      </div>
    );
  }

  renderIssues() {
    const count = (this.state.applyProgress || {}).failed_rows || 0;
    if (!count || this.state.importType !== "package_only") { return null; }
    return (
      <div className="package-import-issues" role="alert">
        <strong>{count} customer rows skipped</strong>
        <p>These customers could not be matched safely. Other rows are processed normally. Fix all listed matches, then upload only the corrected rows; successful assignments are already saved.</p>
        <Button type="button" bsStyle="danger" onClick={() => this.downloadSkippedReport({ import_id: this.state.importId, import_type: "package_only" })}>Download all issues CSV</Button>
        <h5>First 25 issues</h5>
        <Table responsive>
          <thead><tr><th>Row</th><th>Mobile</th><th>Email</th><th>Issue</th></tr></thead>
          <tbody>{(this.state.issueRows || []).map((row) => (
            <tr key={row.row_number}><td>{row.row_number}</td><td>{row.normalized && row.normalized.mobile_no}</td><td>{row.normalized && row.normalized.email}</td><td>{row.error}</td></tr>
          ))}</tbody>
        </Table>
      </div>
    );
  }

  renderRows() {
    const rows = this.state.applyRows ? this.state.applyRows.slice(0, 25) : [];

    if (rows.length === 0) {
      return null;
    }

    return (
      <Table striped responsive>
        <thead>
          <tr>
            <th>Row</th>
            <th>Status</th>
            <th>Action</th>
            <th>Mobile</th>
            <th>Name</th>
            <th>End Date</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.row_number}>
              <td>{row.row_number}</td>
              <td>{row.status}</td>
              <td>{row.action}</td>
              <td>{row.normalized && row.normalized.mobile_no}</td>
              <td>{row.normalized && row.normalized.name}</td>
              <td>{row.package_end_date}</td>
              <td>
                {row.error ||
                  (row.errors && row.errors.length > 0 ? row.errors.join(", ") : "-")}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderImportHistory() {
    if (this.state.importHistory.length === 0) {
      return <div>No imports found.</div>;
    }

    return (
      <div className="package-import-history-scroll"><Table striped className="package-import-history-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Package</th>
            <th>Status</th>
            <th>Progress</th>
            <th>Failed</th>
            <th>Error</th>
            <th>Finished</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {this.state.importHistory.map((item) => (
            (() => {
              const summary = item.apply_summary || {};
              const assignmentTotal = item.import_type === "move_to_basic" ? 0 : item.display_total_rows || summary.package_assignment_total || item.total_rows || 0;
              const assignmentProcessed = item.import_type === "move_to_basic" ? 0 : item.display_processed_rows || summary.package_assignment_processed || item.processed_rows || 0;
              const basicTotal = item.display_basic_total_rows || summary.move_to_basic_total || 0;
              const basicProcessed = item.display_basic_processed_rows || summary.move_to_basic_processed || 0;
              return (
                <tr key={item.import_id}>
                  <td className="history-file">{item.file_name || item.import_id}</td>
                  <td>{item.import_type === "package_only" ? "Packages from Excel" : item.package_title || item.package_id || "-"}</td>
                  <td>
                    <span className={`history-status status-${item.status === "completed" && item.import_type === "package_only" && item.failed_rows > 0 ? "issues" : item.status}`}>{({ apply_failed: "Failed", rows_uploaded: "Ready to apply", processing_batches: "Processing", created: "Awaiting upload", completed: item.import_type === "package_only" && item.failed_rows > 0 ? "Completed with issues" : "Completed" })[item.status] || item.status.replace(/_/g, " ")}</span>

                  </td>
                  <td className="history-progress">
                    {item.import_type !== "move_to_basic" && <span>{assignmentProcessed} / {assignmentTotal}</span>}
                    {(basicTotal > 0 || item.import_type === "move_to_basic") && (
                      <div>Basic: {basicProcessed} / {basicTotal || item.total_rows || 0}</div>
                    )}
                  </td>
                  <td>{item.failed_rows || summary.failed_count || 0}</td>
                  <td className="history-error">{item.error_message || (summary.move_to_basic_missing_lead_count > 0 ? `${summary.move_to_basic_missing_lead_count} missing leads skipped. See report.` : item.import_type === "package_only" && item.failed_rows > 0 ? `${item.failed_rows} customer rows skipped. See issues report.` : "—")}</td>
                  <td className="history-date">{item.completed_at || item.failed_at || "—"}</td>
                  <td><div className="history-actions">
                    {["package_only", "move_to_basic"].indexOf(item.import_type) !== -1 &&
                      ["processing_batches", "apply_failed", "completed", "rows_uploaded"].indexOf(item.status) !== -1 && (
                      <Button type="button" bsStyle="default" disabled={this.state.applying || this.state.movingToBasic || this.state.uploading || !!this.state.deletingTask}
                        onClick={() => this.viewOperation(item)}>
                        {item.status === "rows_uploaded" ? "Apply Import" : item.status === "apply_failed" ? "Resume" : "View progress"}
                      </Button>
                    )}
                    {["package_only", "move_to_basic"].indexOf(item.import_type) !== -1 &&
                      ["created", "rows_uploaded", "apply_failed", "completed"].indexOf(item.status) !== -1 && (
                      <Button type="button" bsStyle="danger" disabled={this.state.applying || this.state.movingToBasic || this.state.uploading || !!this.state.deletingTask}
                        onClick={() => this.deleteTask(item)}>
                        {this.state.deletingTask === item.import_id ? "Deleting…" : "Delete task"}
                      </Button>
                    )}
                    {item.import_type === "package_only" && item.failed_rows > 0 && (
                      <Button type="button" bsStyle="danger" onClick={() => this.downloadSkippedReport(item)}>Issues CSV</Button>
                    )}
                    {item.import_type === "move_to_basic" && (item.failed_rows > 0 || summary.move_to_basic_missing_lead_count > 0) && (
                      <Button type="button" bsStyle="default" onClick={() => this.downloadSkippedReport(item)}>Skipped members CSV</Button>
                    )}
                  </div></td>
                </tr>
              );
            })()
          ))}
        </tbody>
      </Table></div>
    );
  }

  render() {
    return (
      <div className="main-content">
        <Grid fluid>
          <NotificationSystem ref="notificationSystem" style={style} />
          <Row>
            <Col md={12} lg={4}>
              <Card
                content={
                  <form>
                    <Row>
                      <Col md={12}>
                        <h5>1. Move package members to BASIC</h5>
                        <FormGroup>
                          <ControlLabel>{this.state.importType === "package_only" ? "Select Package (Move to BASIC)" : "Select Package"}</ControlLabel>
                          <Select
                            className="react-select primary"
                            classNamePrefix="react-select"
                            placeholder="Select package"
                            isClearable
                            value={this.selectedPackageOption()}
                            options={this.packageOptions()}
                            onChange={this.handlePackageSelectChange}
                            isDisabled={this.isImportLocked()}
                          />
                          <Button
                            type="button"
                            bsStyle="warning"
                            style={{ marginTop: 10 }}
                            disabled={!this.state.packageId || String(this.state.packageId) === "24" || this.isImportLocked()}
                            onClick={this.moveToBasic}
                          >
                            {this.state.movingToBasic ? "Moving to BASIC…" : ["apply_failed", "request_failed"].indexOf(this.state.basicStatus) !== -1 ? "Retry / Resume move to BASIC" : "Move to BASIC"}
                          </Button>
                          <div style={{ marginTop: 8 }}>
                            No Excel is required. Moves active members of the selected package to BASIC. Wait for completion before importing.
                          </div>
                          {this.state.basicStatus && (
                            <div style={{ marginTop: 8 }}>
                              Move to BASIC: {this.state.basicStatus === "request_failed" ? "Request could not be confirmed" : this.state.basicStatus}
                              {this.state.basicSummary && (
                                <span> ({this.state.basicSummary.move_to_basic_processed || 0} / {this.state.basicSummary.move_to_basic_total || 0})</span>
                              )}
                            </div>
                          )}
                          {this.state.basicError && (
                            <div role="alert" className="text-danger" style={{ marginTop: 8 }}>{this.state.basicError}</div>
                          )}
                          {this.state.basicSummary && this.state.basicSummary.move_to_basic_missing_lead_count > 0 && (
                            <div style={{ marginTop: 16 }}>
                              <strong>{this.state.basicSummary.move_to_basic_success_count || 0} moved to BASIC; {this.state.basicSummary.move_to_basic_missing_lead_count} skipped because the lead no longer exists.</strong>
                              <p>Orphaned memberships were deactivated. No replacement customers were created.</p>
                              <Button type="button" bsStyle="default" onClick={() => this.downloadSkippedReport({ import_id: this.state.basicImportId })}>Download all skipped members</Button>
                              <h5>First 25 skipped members</h5>
                              <Table responsive striped>
                                <thead><tr><th>Lead ID</th><th>Membership ID</th><th>Reason</th></tr></thead>
                                <tbody>{this.state.basicSkippedRows.map((row, index) => (
                                  <tr key={index}><td>{row.lead_id}</td><td>{row.normalized && row.normalized.membership_id}</td><td>{row.error}</td></tr>
                                ))}</tbody>
                              </Table>
                            </div>
                          )}
                        </FormGroup>

                        <h5 style={{ marginTop: 25 }}>2. Import package assignments</h5>
                        <FormGroup>
                          <ControlLabel>Lead Assign Type</ControlLabel>
                          <FormControl
                            componentClass="select"
                            name="importType"
                            value={this.state.importType}
                            onChange={this.handleInputChange}
                            disabled={this.isImportLocked()}
                          >
                            <option value="package_wallet">
                              Assign package + wallet balance
                            </option>
                            <option value="package_family_wallet">
                              Assign package + family members + wallet balance
                            </option>
                            <option value="package_only">Assign package only (package names from Excel)</option>
                          </FormControl>
                        </FormGroup>

                        <FormGroup>
                          <ControlLabel>Organization</ControlLabel>
                          <div className="form-control" style={{ height: "auto", minHeight: 40 }}>
                            {this.state.importType === "package_only" ? "Determined by each catalogue package in Excel" : this.renderOrganizations()}
                          </div>
                        </FormGroup>

                        <FormGroup>
                          <ControlLabel>Package End Date</ControlLabel>
                          <FormControl
                            type="date"
                            name="packageEndDate"
                            value={this.state.packageEndDate}
                            onChange={this.handleInputChange}
                            disabled={this.isImportLocked()}
                          />
                        </FormGroup>

                        {this.state.importType === "package_only" && (
                          <div style={{ marginBottom: 15 }}>
                            One Excel can contain multiple packages. Each row must contain its exact catalogue package name in APPLICABLE_PACKAGE or a Package Name column.
                            {" "}Customers are matched by mobile number or email. Policy numbers are saved for reference and can repeat.
                            This import assigns only customers in the file, without wallet credits. Use Move to BASIC separately before importing if needed.
                          </div>
                        )}

                        <FormGroup>
                          <ControlLabel>Package Leads Excel</ControlLabel>
                          <br></br>
                          <input
                            style={{ display: "none" }}
                            id="selectPackageLeadExcel"
                            type="file"
                            className="form-control"
                            name="profile"
                            accept=".xlsx,.xls,.csv"
                            onChange={this.handleFileChange}
                          />
                          <Button
                            disabled={this.isImportLocked()}
                            onClick={() =>
                              document.getElementById("selectPackageLeadExcel").click()
                            }
                            style={{ color: "#2B3A41" }}
                          >
                            {" "}
                            <i
                              className={
                                this.state.file == null
                                  ? "fa fa-plus"
                                  : "fa fa-pencil fa-fw"
                              }
                            ></i>{" "}
                            {this.state.file == null
                              ? "Choose Excel"
                              : "Change Excel"}
                          </Button>
                        </FormGroup>

                        <Button
                          className="btn-color"
                          fill
                          disabled={this.state.uploading || this.isImportLocked()}
                          onClick={this.upload}
                        >
                          <i className="fa fa-upload" />{" "}
                          {this.state.uploading ? "Uploading" : "Upload"}
                        </Button>
                        {(this.state.uploading || this.state.uploadProgress > 0) && (
                          <div style={{ marginTop: 10 }}>
                            Upload progress: {this.state.uploadProgress || 0}%
                          </div>
                        )}
                        {this.state.importId && !this.state.uploadReady && this.state.importType !== "package_only" && (
                          <div style={{ marginTop: 10 }}>
                            Server is preparing the uploaded file. You can save the mapping and apply now.
                          </div>
                        )}
                      </Col>
                    </Row>

                    {this.state.headers.length > 0 && (
                      <div style={{ marginTop: 20 }}>
                        <h5>Column Mapping</h5>
                        <Row>
                          {Object.keys(this.mappingFields()).map((field) => (
                            <Col md={12} key={field}>
                              <FormGroup>
                                <ControlLabel>
                                  {this.mappingFields()[field]}
                                </ControlLabel>
                                <FormControl
                                  componentClass="select"
                                  value={this.state.mapping[field] || ""}
                                  disabled={this.isImportLocked()}
                                  onChange={(event) =>
                                    this.handleMappingChange(field, event.target.value)
                                  }
                                >
                                  {this.renderHeaderOptions()}
                                </FormControl>
                              </FormGroup>
                            </Col>
                          ))}
                        </Row>
                        <Button
                          className="btn-color"
                          fill
                          bsStyle="danger"
                          disabled={this.state.applying || (!this.state.importId && this.state.importType !== "package_only") || this.isImportLocked()}
                          onClick={this.apply}
                        >
                          <i className="fa fa-check" />{" "}
                          {this.state.applying
                            ? "Processing"
                            : this.state.importId || this.state.importType === "package_only"
                              ? "Apply Import"
                              : "Waiting for Upload"}
                        </Button>
                      </div>
                    )}

                    {this.canStartNewImport() && (
                      <Button type="button" bsStyle="default" style={{ marginTop: 10 }} onClick={this.resetImport}>
                        <i className="fa fa-refresh" /> New Import
                      </Button>
                    )}

                    {(this.state.applyStatus || this.state.applySummary) && (
                      <div style={{ marginTop: 25 }}>
                        <h5>Import Status</h5>
                        {this.renderApplyStatus()}
                        {this.state.applySummary && <h5 style={{ marginTop: 25 }}>Import Summary</h5>}
                        {this.renderSummary()}
                        {this.renderIssues()}
                        {this.state.applyRows.length > 0 && <h5 style={{ marginTop: 25 }}>First 25 Rows</h5>}
                        {this.renderRows()}
                      </div>
                    )}
                  </form>
                }
              />
            </Col>
            <Col md={12} lg={8}>
              <Card
                content={
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h5>Import History</h5>
                      <Button
                        bsStyle="default"
                        disabled={this.state.historyLoading}
                        onClick={this.loadImportHistory}
                      >
                        <i className="fa fa-refresh" />{" "}
                        {this.state.historyLoading ? "Refreshing" : "Refresh"}
                      </Button>
                    </div>
                    {this.renderImportHistory()}
                  </div>
                }
              />
            </Col>
          </Row>
        </Grid>
      </div>
    );
  }
}

export default PackageLeadImport;
